import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, NoCompanyGuard, PublicRouteGuard } from '@/common/guards';
import { PreventDirtyFormGuard } from '@/common/guards/dirty-form.guard';
import { canActivatePermission } from '@/common/guards/permission.guard';
import { PERMISSIONS } from '@/common/models/permission.model';
import { LayoutComponent } from './layout/layout.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/landing/landing-user/landing-user.module').then(m => m.LandingUserModule),
    data: { title: 'LANDING.META.TITLE' },
  },
  {
    path: 'auth',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/auth/auth.module').then(m => m.AuthModule),
  },
  {
    path: 'register',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/app-link-register/app-link-register.module').then(m => m.AppLinkRegisterModule),
    data: { title: 'APP_LINK_REGISTER.META.TITLE' },
  },
  {
    // Mismo flujo que 'register', pero con el nombre de la empresa como prefijo cosmético
    // en la URL (wokap.com/[empresa]/register/[codigo]). La oferta se resuelve igual por
    // shortUrlId; el segmento de empresa no se valida.
    path: ':company/register',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/app-link-register/app-link-register.module').then(m => m.AppLinkRegisterModule),
    data: { title: 'APP_LINK_REGISTER.META.TITLE' },
  },
  {
    path: 'errors',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/errors/errors.module').then(m => m.ErrorsModule),
  },
  {
    path: 'landing-company',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/landing/landing-company/landing-company.module').then(m => m.LandingCompanyModule),
    data: { title: 'LANDING_COMPANY.META.TITLE' },
  },
  {
    path: 'search',
    canActivate: [PublicRouteGuard],
    loadComponent: () => import('@/pages/landing/public-search/public-search.component').then(m => m.PublicSearchComponent),
    data: { title: 'PUBLIC_SEARCH.META.TITLE' },
  },
  {
    path: 'privacy-policy',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/landing/privacy/privacy.module').then(m => m.PrivacyModule),
    data: { title: 'LANDING.PRIVACY.META.TITLE' },
  },
  {
    path: 'terms-and-conditions',
    canActivate: [PublicRouteGuard],
    loadChildren: () => import('@/pages/landing/terms/terms.module').then(m => m.TermsModule),
    data: { title: 'LANDING.TERMS.META.TITLE' },
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'navigation',
        loadComponent: () => import('@/pages/navigation/components/navigation/navigation.component')
          .then(m => m.NavigationComponent),
        canActivate: [NoCompanyGuard],
        canDeactivate: [PreventDirtyFormGuard],
        data: {
          navbarItem: 'navigation',
          title: 'NAVIGATION.TITLE',
          path: 'navigation',
        },
      },
      {
        // Demo-only route: Shorts-style redesign pitch, kept on its own branch/route so it never
        // affects the production /navigation route above.
        path: 'navigation-shorts',
        loadComponent: () => import('@/pages/navigation/components/navigation-shorts/navigation-shorts.component')
          .then(m => m.NavigationShortsComponent),
        canActivate: [NoCompanyGuard],
        canDeactivate: [PreventDirtyFormGuard],
        data: {
          navbarItem: 'navigation',
          title: 'NAVIGATION.SHORTS_TITLE',
          path: 'navigation-shorts',
        },
      },
      {
        path: 'dashboard',
        loadChildren: () => import('@/pages/dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [canActivatePermission],
        data: {
          navbarItem: 'dashboard',
          title: 'DASHBOARD.TITLE',
          path: 'dashboard',
          permissions: [PERMISSIONS.ENTITY.VIEW_ALL, PERMISSIONS.USER.VIEW_ALL], // Uso interno (backoffice)
        },
      },
      {
        path: 'offers',
        loadChildren: () => import('@/pages/offers/offers.module').then(m => m.OffersModule),
        // Guard de permisos removido - navegación libre desde menú lateral
        canActivate: [NoCompanyGuard],
        data: {
          navbarItem: 'offers',
          title: 'OFFERS.TITLE',
          path: 'offers',
          permissions: [PERMISSIONS.OFFER.VIEW],
        },
      },
      {
        path: 'chat',
        loadChildren: () => import('@/pages/chat/chat.module').then(m => m.ChatModule),
        // Guard de permisos removido - navegación libre desde menú lateral
        canActivate: [NoCompanyGuard],
        data: {
          navbarItem: 'chat',
          title: 'CHAT.TITLE',
          path: 'chat',
          permissions: [PERMISSIONS.APPLY_OFFER.VIEW],
        },
      },
      {
        path: 'company',
        loadChildren: () => import('@/pages/companies/companies.module').then(m => m.CompaniesModule),
        data: { title: 'COMPANIES.TITLE', path: 'company' },
        // Guard de permisos removido - navegación libre desde menú lateral
      },
      {
        path: 'user-management',
        // Guard de permisos removido - navegación libre desde menú lateral
        canActivate: [NoCompanyGuard],
        loadChildren: () => import('@/pages/user-management/user-management.module').then(m => m.UserManagementModule),
        data: {
          title: 'USER-MANAGEMENT.TITLE',
          path: 'user-management',
          // permissions removido - sin restricciones de navegación
        },
      },
      {
        path: 'entity-location',
        canActivate: [NoCompanyGuard],
        loadChildren: () => import('@/pages/entity-location/entity-location.module').then(m => m.EntityLocationModule),
        data: {
          title: 'ENTITY-LOCATION.TITLE',
          path: 'entity-location',
        },
      },
      {
        path: 'individual-user-management',
        // Guard de permisos removido - navegación libre desde menú lateral
        loadChildren: () => import('@/pages/individual-user-management/individual-user-management.module').then(m => m.IndividualUserManagementModule),
        data: {
          navbarItem: 'individual-user-management',
          title: 'INDIVIDUAL-USER-MANAGEMENT.TITLE',
          path: 'individual-user-management',
          permissions: [PERMISSIONS.USER.VIEW],
        },
      },
      {
        path: 'saas-training',
        loadChildren: () => import('@/pages/saas-training/saas-training.module').then(m => m.SaasTrainingModule),
        canActivate: [canActivatePermission],
        data: {
          navbarItem: 'saas-training',
          title: 'SAAS-TRAINING.TITLE',
          path: 'saas-training',
          permissions: [PERMISSIONS.USER.VIEW_ALL], // Uso interno (backoffice)
        },
      },
      {
        path: 'user-action-log',
        loadChildren: () => import('@/pages/user-action-log/user-action-log.module').then(m => m.UserActionLogModule),
        data: {
          title: 'USER-ACTION-LOG.TITLE',
          path: 'user-action-log',
        },
      },
      {
        path: 'ai-connector',
        loadComponent: () => import('@/pages/ai-connector/ai-connector.component').then(m => m.AiConnectorComponent),
        data: {
          navbarItem: 'ai-connector',
          title: 'AI-CONNECTOR.TITLE',
          path: 'ai-connector',
        },
      },
      {
        path: 'wokap-ia',
        loadComponent: () => import('@/pages/wokap-ia/components/wokap-ia-wizard/wokap-ia-wizard.component')
          .then(m => m.WokapIaWizardComponent),
        canDeactivate: [PreventDirtyFormGuard],
        data: {
          navbarItem: 'wokap-ia',
          title: 'WOKAP_IA.WIZARD.NAVBAR_TITLE',
          path: 'wokap-ia',
        },
      },
      // TikTok ahora está dentro del módulo de Offers como /offers/detail/:candidateId
      // {
      //   path: 'tiktok',
      //   loadChildren: () => import('@/pages/tiktok/tiktok.module').then(m => m.TiktokModule),
      //   data: {
      //     navbarItem: 'tiktok',
      //     title: 'TikTok Demo',
      //     path: 'tiktok',
      //   },
      // },
      {
        path: 'profile',
        loadChildren: () => import('@/pages/auth/auth.module').then(m => m.AuthModule),
        data: { title: 'AUTH.PROFILE.TITLE' },
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/errors/not_found',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
